import dayjs from 'dayjs';
import { TFunction } from 'i18next';

import { PaymentStatus, SubscriptionTypes } from 'constants/enums';
import { FALLBACK_NUMBER_VALUE, FALLBACK_VALUE } from 'constants/uiSettings';

export type SubscriptionIcons = 'ok' | 'warn' | 'close' | 'slash';
type SubscriptionStatusUI = {
  iconType: null | SubscriptionIcons;
  text: string;
  tooltip: string;
};

const JOB_SUBSCRIPTION_DAYS_TO_WARN = 7;
const FAILED_STATUSES = new Set<PaymentStatus>([
  PaymentStatus.Unpaid,
  PaymentStatus.Paused,
  PaymentStatus.Trialing,
  PaymentStatus.Incomplete,
  PaymentStatus.IncompleteExpired,
  PaymentStatus.PastDue
]);

const DEFAULT_DATA: SubscriptionStatusUI = {
  iconType: null,
  text: FALLBACK_VALUE,
  tooltip: ''
};

export const mapSubscriptionToStatusUI = (
  t: TFunction<'common', undefined>,
  subscriptionType: SubscriptionTypes | null,
  status: PaymentStatus | null,
  dueDate: string | null
): SubscriptionStatusUI => {
  if (subscriptionType === SubscriptionTypes.Free) {
    return {
      ...DEFAULT_DATA,
      text: t('label.free')
    };
  }

  if (subscriptionType === SubscriptionTypes.Concierge) {
    return DEFAULT_DATA;
  }

  if (!status) {
    return DEFAULT_DATA;
  }

  if (status === PaymentStatus.Active) {
    if (!dueDate) {
      return DEFAULT_DATA;
    }

    const isSubscriptionDueDateTomorrow = dayjs(dueDate).isSame(
      dayjs().add(1, 'day'),
      'day'
    );
    const daysRemaining = dayjs(dueDate).diff(dayjs(), 'days');
    const daysRemainingCorrected =
      daysRemaining >= 0 ? daysRemaining : FALLBACK_NUMBER_VALUE;

    const subscriptionOverSoon =
      daysRemaining === 0 || isSubscriptionDueDateTomorrow;

    const isSafePeriod = daysRemaining > JOB_SUBSCRIPTION_DAYS_TO_WARN;

    return {
      tooltip: isSafePeriod
        ? t('jobSubscriptionStatus.tooltip.active.safe')
        : t('jobSubscriptionStatus.tooltip.active.notSafe'),
      text: subscriptionOverSoon
        ? t('jobSubscriptionStatus.text.active.dueDate')
        : t('jobSubscriptionStatus.text.active.notSoon', {
            count: +daysRemainingCorrected
          }),
      iconType: isSafePeriod ? 'ok' : 'warn'
    };
  }

  if (status === PaymentStatus.Canceled) {
    return {
      iconType: 'close',
      text: t('jobSubscriptionStatus.text.canceled'),
      tooltip: t('jobSubscriptionStatus.tooltip.canceled')
    };
  }

  if (FAILED_STATUSES.has(status)) {
    return {
      ...DEFAULT_DATA,
      iconType: 'slash',
      text: t('jobSubscriptionStatus.text.pastDue')
    };
  }

  return DEFAULT_DATA;
};
